import * as React from 'react';
import { useRecoilState } from 'recoil';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import type { HiveWorkingBranch, HiveWorkingDocument } from '@gitbook/hive-client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import {
    createPalette,
    PaletteButton,
    type PaletteCommandDef,
    PaletteCommandType,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    ButtonSkeleton,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    LoadingIndicator,
    toast,
    useDialogAction,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import {
    type LoadingStateHookResult,
    useEventCallback,
    useLiveRef,
    useLoadingState,
} from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { apiClient, mergeStrategyAtom } from '../../state';
import { useGitSyncCurrentStatus } from '../GitSyncFlow';
import { GitSyncProviderName } from '../GitSyncProviders';
import { useImportRuns } from '../Imports';
import { useEvaluateMergeRules } from '../MergeRules';
import {
    GuidedTourPopover,
    GuidedTourReference,
    GuidedTourStep,
    useGuidedTour,
} from '../Onboarding';
import { ChangeRequestMergeRulesStatusButton } from './ChangeRequestMergeRulesStatus';
import { ChangeRequestReviewRequestDialog } from './ChangeRequestReviewRequestDialog';

type ChangeRequestMergeButtonsProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;

    /**
     * If true, a button to summarize the merge rules status will be shown.
     */
    showMergeRulesStatus?: boolean;
};

/**
 * Button to merge or update a change request.
 */
export function ChangeRequestMergeButtons(props: ChangeRequestMergeButtonsProps) {
    const { space, changeRequest, analyticsContext } = props;

    const { navigate } = useNavigation();
    const hiveClient = useHiveClient();
    const { revision } = useHiveRevision();

    const requestReviewDialogTrigger = useDialogTriggerState();

    const updateLoadable = useLoadingState();
    const [loadingUpdate, wrapLoadingUpdate] = updateLoadable;

    const mergeLoadable = useLoadingState();
    const [loadingMerge] = mergeLoadable;

    // When merging, the main branch will be updated before the change-request is updated.
    // we wait for the merge to finish before deciding if we should consider the CR as out of date.
    const isOutOfDateBeforeMergingRef = React.useRef(changeRequest.outdated);
    React.useLayoutEffect(() => {
        if (!loadingMerge) {
            isOutOfDateBeforeMergingRef.current = changeRequest.outdated;
        }
    });
    const isOutOfDate = loadingMerge ? isOutOfDateBeforeMergingRef.current : changeRequest.outdated;

    const isResolvingConflicts = hiveCore.isConflictRevision(revision);
    const hasConflicts = hiveCore.isConflictRevision(revision);
    const canRequestAReview = changeRequest.status === api.ChangeRequestStatus.Draft;

    /** User pressed button to request a review on their CR */
    const onRequestReview = () => {
        requestReviewDialogTrigger.open();
    };

    /** User updated the CR with changes on primary content */
    const onUpdateFromPrimary = useEventCallback(async () => {
        // Blur the document to flush and title/description changes.
        blurDocument();

        closeNotifyOutOfDateRef.current?.();

        await wrapLoadingUpdate(async () => {
            await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                await toast.catch(async () => {
                    const {
                        data: { result },
                    } = await apiClient.changeRequests.updateChangeRequest(
                        space.id,
                        changeRequest.id
                    );

                    if (result !== 'conflicts') {
                        toast.success('Change request updated', {
                            description:
                                'Changes made recently on the space have been updated in the change request.',
                        });
                    }
                }, 'Error while updating change request');
            }, checkIsAffectedSyncedFactory({ space, changeRequest }));
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'change_request_update',
            changeRequest: changeRequest.id,
        });
    });

    const mergeChangeRequest = async () => {
        await toast.catch(async () => {
            await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                const { data } = await apiClient.changeRequests.mergeChangeRequest(
                    space.id,
                    changeRequest.id
                );

                if (data.result === 'merge') {
                    toast('Change request merged', {
                        description: `${
                            space.title || 'The space'
                        } has been updated with the changes.`,
                        icon: Icon.ChangeRequestMerged,
                    });

                    navigate({
                        screen: {
                            space: space.id,
                            changeRequest: undefined,
                            revision: undefined,
                            mode: undefined,
                            sideSheet: undefined,
                        },
                    });
                }
            }, checkIsAffectedSyncedFactory({ space, changeRequest }));
        }, 'Error while merging change request');
    };

    const confirmOutdatedMergeDialogTrigger = useDialogTriggerState();

    const onMerge = async () => {
        // Blur the document to flush and title/description changes.
        blurDocument();

        if (isOutOfDate) {
            confirmOutdatedMergeDialogTrigger.open();
        } else {
            await mergeChangeRequest();
        }
    };

    //
    // Notify the user that the CR is out of date.
    //
    const closeNotifyOutOfDateRef = React.useRef<null | (() => void)>(null);
    const isResolvingConflictsRef = useLiveRef(isResolvingConflicts);
    React.useEffect(() => {
        if (isOutOfDate && !isResolvingConflictsRef.current) {
            const id = toast('Change request out of date', {
                description:
                    'This space has been edited since this change request was last updated.',
                action: <Button label="Update" onPress={onUpdateFromPrimary} />,
            });
            const clearToast = () => toast.dismiss(id);
            closeNotifyOutOfDateRef.current = clearToast;

            return () => {
                closeNotifyOutOfDateRef.current = null;
                clearToast();
            };
        }
    }, [isOutOfDate, isResolvingConflictsRef, onUpdateFromPrimary]);

    if (hasConflicts) {
        return null;
    }

    return (
        <>
            <DialogRoot {...requestReviewDialogTrigger.rootProps}>
                <ChangeRequestReviewRequestDialog changeRequest={changeRequest} space={space} />
            </DialogRoot>

            {isOutOfDate ? (
                <>
                    <Button
                        title={`This change request is out of date with the space's primary content.\nUpdate to bring the latest edits into this change request.`}
                        kind="secondary"
                        size="medium"
                        onPress={onUpdateFromPrimary}
                        label="Update"
                        className="max-md:hidden"
                        loadable={updateLoadable}
                        disabled={loadingMerge}
                    />
                    <DialogRoot {...confirmOutdatedMergeDialogTrigger.rootProps}>
                        <ConfirmOutOfDateMergeDialog
                            onUpdate={onUpdateFromPrimary}
                            onMerge={mergeChangeRequest}
                        />
                    </DialogRoot>
                </>
            ) : null}

            <React.Suspense
                fallback={
                    // We need a suspense boundary as the evaluation of the merge rule can be async
                    <ButtonSkeleton label="Merge" />
                }
            >
                <ChangeRequestMergeButton
                    {...props}
                    canRequestAReview={canRequestAReview}
                    loadable={mergeLoadable}
                    disabled={loadingUpdate}
                    onPressReadyForReview={onRequestReview}
                    onMerge={onMerge}
                />
            </React.Suspense>
        </>
    );
}

function ConfirmOutOfDateMergeDialog(props: {
    onUpdate: () => Promise<void>;
    onMerge: () => Promise<void>;
}) {
    const { onUpdate, onMerge } = props;
    const [isPending, startDialogAction] = useDialogAction();
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Out of date change request</Heading>
            </DialogHeader>
            <DialogBody>
                This change request is out of date with the space’s primary content. Do you want to
                update it with the latest changes before merging?
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    label="Merge anyway"
                    kind="secondary"
                    disabled={isPending}
                    onPress={async () => {
                        await startDialogAction(async ({ close }) => {
                            await onMerge();
                            close();
                        });
                    }}
                />
                <Button
                    label="Update"
                    disabled={isPending}
                    onPress={async () => {
                        await startDialogAction(async ({ close }) => {
                            await onUpdate();
                            close();
                        });
                    }}
                />
            </DialogFooter>
        </Dialog>
    );
}

/**
 * Button to merge a change request into its space.
 */
function ChangeRequestMergeButton(
    props: ChangeRequestMergeButtonsProps & {
        disabled?: boolean;
        canRequestAReview: boolean;
        onPressReadyForReview: () => void;
        onMerge: () => Promise<void>;
        className?: string;
        loadable: LoadingStateHookResult;
    }
) {
    const {
        space,
        changeRequest,
        analyticsContext,
        disabled: propDisabled = false,
        canRequestAReview,
        onPressReadyForReview,
        onMerge,
        className,
        loadable,
        showMergeRulesStatus = false,
    } = props;

    const [isLoading] = loadable;

    const gitSyncStatus = useGitSyncCurrentStatus(space);
    const [mergeStrategy, setMergeStrategy] = useRecoilState(mergeStrategyAtom);
    const { navigate } = useNavigation();

    const importRuns = useImportRuns({
        target: {
            space: space.id,
            changeRequest: changeRequest.id,
        },
    });
    const showImportGuidedTour = importRuns.length > 0;
    const disabledBecauseOfImport = importRuns.some((run) =>
        ['pending', 'in-progress'].includes(run.status)
    );
    const disabledBecauseOfImportMessaging = 'Merging is blocked while an import is running';

    const isGitSyncRunning = gitSyncStatus?.state === 'running';
    const latestGitSyncImportWasErrorneous = Boolean(
        space.gitSync?.installationProvider &&
            (gitSyncStatus?.state === 'failure' || gitSyncStatus?.state === 'timed-out') &&
            gitSyncStatus.direction === api.GitSyncOperationDirection.Import
    );

    // Merge button is disabled when the merge rule is rejected
    const mergeRulesEvaluation = useEvaluateMergeRules({ space, changeRequest });
    const disabledBecauseOfMergeRules = mergeRulesEvaluation.result === 'rejected';

    // Merge button is disabled when Git Sync is running
    const disabledBecauseOfGitSync = mergeStrategy === 'merge' && isGitSyncRunning;
    const disabledBecauseOfGitSyncMessaging = 'Git Sync in progress, please wait to merge';

    // Merge button is disabled when the latest import has failed
    const disabledBecauseOfGitImportError =
        mergeStrategy === 'merge' && latestGitSyncImportWasErrorneous;
    const provider = GitSyncProviderName[space.gitSync?.installationProvider ?? 'github'];
    const disabledBecauseOfGitImportErrorMessaging = `An error was detected while importing recent commits from the ${provider} repository, to resume synchronization and merging of change requests please fix the error.`;

    const strategyConfigs = [
        {
            strategy: 'merge' as const,
            available:
                space.editMode === api.SpaceEditMode.Live
                    ? space.permissions.edit
                    : space.permissions.merge,
            title: 'Merge',
            subtitle: disabledBecauseOfMergeRules
                ? 'Merging is blocked because of merge rules'
                : disabledBecauseOfGitSync
                  ? disabledBecauseOfGitSyncMessaging
                  : disabledBecauseOfImport
                    ? disabledBecauseOfImportMessaging
                    : 'Instantly merge your changes.',
            icon: Icon.ChangeRequestMerged,
            onPress: onMerge,
            label: 'Merge',
            kind:
                disabledBecauseOfImport || isGitSyncRunning || latestGitSyncImportWasErrorneous
                    ? ('secondary' as const)
                    : ('merge' as const),
            disabledReason: disabledBecauseOfMergeRules
                ? `This change request does not meet the merge rule: ${mergeRulesEvaluation.rejectedRules[0].message}`
                : disabledBecauseOfGitSync
                  ? disabledBecauseOfGitSyncMessaging
                  : disabledBecauseOfGitImportError
                    ? disabledBecauseOfGitImportErrorMessaging
                    : disabledBecauseOfImport
                      ? disabledBecauseOfImportMessaging
                      : undefined,
        },
        {
            strategy: 'ready-for-review' as const,
            available: canRequestAReview,
            title: 'Request a review',
            subtitle: 'Notify and allow others to review before merging',
            icon: Icon.ChangeRequestOpen,
            onPress: onPressReadyForReview,
            label: 'Request a review',
            kind: 'primary' as const,
            disabledReason: undefined,
        },
    ].filter((config) => config.available);

    // We show as selected only a strategy that can actually be used
    const workingStrategyConfigs = strategyConfigs.filter((config) => !config.disabledReason);
    const activeConfig =
        workingStrategyConfigs.find((config) => config.strategy === mergeStrategy) ??
        workingStrategyConfigs[0] ??
        strategyConfigs[0];

    const importTour = useGuidedTour('cr-import');

    if (!activeConfig) {
        return null;
    }

    const disabled = isLoading || propDisabled || Boolean(activeConfig.disabledReason);

    return (
        <div className="flex flex-row gap-1">
            <GuidedTourStep tour="cr-import" skip={!showImportGuidedTour} step="start">
                <GuidedTourReference>
                    <ButtonGroup
                        kind="segmented"
                        disabled={disabled}
                        className={className}
                        separatorKind={activeConfig.kind}
                        border={false}
                    >
                        <Button
                            kind={activeConfig.kind}
                            leadingIcon={activeConfig.icon}
                            disabled={disabled}
                            tooltip={
                                activeConfig.disabledReason
                                    ? { label: activeConfig.disabledReason }
                                    : undefined
                            }
                            label={activeConfig.label}
                            loadable={loadable}
                            onPress={() => {
                                importTour.markAsDone();
                                return activeConfig.onPress();
                            }}
                        />

                        {strategyConfigs.length > 1 ? (
                            <PaletteButton
                                kind={activeConfig.kind}
                                leadingIcon={Icon.ChevronDown}
                                disabled={disabled}
                                palette={() =>
                                    createPalette({
                                        position: {
                                            width: PalettePositionWidth.LARGE,
                                            preferredPlacement: 'bottom',
                                            preferredAlignment: 'end',
                                        },
                                        groups: [
                                            {
                                                key: 'options',
                                                commands: strategyConfigs.map((config) => {
                                                    const command: PaletteCommandDef = {
                                                        key: config.strategy,
                                                        type: PaletteCommandType.RADIO,
                                                        selected:
                                                            activeConfig.strategy ===
                                                            config.strategy,
                                                        title: config.title,
                                                        subtitle: config.subtitle,
                                                        icon: isLoading
                                                            ? LoadingIndicator
                                                            : config.icon,
                                                        disabled:
                                                            disabled || !!config.disabledReason,
                                                        trigger: () =>
                                                            setMergeStrategy(config.strategy),
                                                    };
                                                    return command;
                                                }),
                                            },
                                            space.permissions.admin
                                                ? {
                                                      key: 'space.mergeRules',
                                                      commands: [
                                                          {
                                                              key: 'space.mergeRules',
                                                              icon: Icon.MergeRulesAllow,
                                                              title: 'Configure merge rules',
                                                              subtitle:
                                                                  'Define the rules for merging change requests',
                                                              trigger: () => {
                                                                  navigate({
                                                                      screen: {
                                                                          type: 'space',
                                                                          space: space.id,
                                                                          sideSheet: {
                                                                              type: 'merge-rule',
                                                                          },
                                                                      },
                                                                  });
                                                              },
                                                          },
                                                      ],
                                                  }
                                                : null,
                                        ].filter(filterOutNullable),
                                    })
                                }
                                iconOnly
                                tooltip={{
                                    disabled,
                                    label: 'Select option or configure merge rules',
                                }}
                                label="Select option or configure merge rules"
                                onPressStart={() => {
                                    analytics.logEvent(
                                        analytics.createChangeRequestEvent(
                                            space,
                                            changeRequest.id,
                                            {
                                                ...analyticsContext,
                                                type: 'change_request_pick_flows_open',
                                                spaceSyncStatus: isGitSyncRunning
                                                    ? 'syncing'
                                                    : 'idle',
                                            }
                                        )
                                    );
                                }}
                            />
                        ) : null}
                    </ButtonGroup>
                </GuidedTourReference>
                <GuidedTourPopover className="max-w-sm">
                    When the imported content is ready to go live, you can either{' '}
                    <strong>Merge</strong> it directly or <strong>Request a review</strong> if you’d
                    like someone to check it first.
                </GuidedTourPopover>
            </GuidedTourStep>
            {disabledBecauseOfMergeRules && showMergeRulesStatus ? (
                <ChangeRequestMergeRulesStatusButton
                    space={space}
                    changeRequest={changeRequest}
                    mergeRulesEvaluation={mergeRulesEvaluation}
                />
            ) : null}
        </div>
    );
}

function checkIsAffectedSyncedFactory(input: {
    space: api.Space;
    changeRequest: api.ChangeRequest;
}) {
    const { space, changeRequest } = input;
    return (sync: HiveWorkingBranch | HiveWorkingDocument) => {
        return (
            sync.contentId === space.id &&
            sync.branchId === getChangeRequestBranchId(changeRequest.id)
        );
    };
}

function blurDocument() {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
    }
}
